import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnDestroy, Optional, Self } from '@angular/core';
import {
    ControlValueAccessor,
    FormGroupDirective,
    NgControl,
    NgForm,
    UntypedFormControl,
    Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { BigNumber } from 'bignumber.js';
import { Subject } from 'rxjs';
import { NO_DECIMAL_PLACES, TWO_DECIMAL_PLACES } from '../../../../constants/constants';
import { NumberUtils } from '../../../../utils/number-utils';

const ARIA_DESCRIBED_BY = 'aria-describedby';
/** Custom `MatFormFieldControlNumberInput` for number input. */
@Component({
    selector: 'number-input',
    templateUrl: 'number-input.html',
    styleUrls: ['number-input.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: NumberInputComponent }],
    host: {
        '[id]': 'id',
    },
})
export class NumberInputComponent implements MatFormFieldControl<number>, ControlValueAccessor, OnDestroy {
    private static nextId = 0;

    inputValueControl = new UntypedFormControl(null);
    readonly stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    readonly id = `number-input-${NumberInputComponent.nextId++}`;
    _disabled = false;
    _placeholder = '';
    displayWarningTriangle = false;

    private _required = false;
    private _onChange: ((value: number | null) => void) | undefined;
    private _onTouched: (() => void) | undefined;
    private readonly parentForm: FormGroupDirective | NgForm | null;

    @Input(ARIA_DESCRIBED_BY) userAriaDescribedBy: string | undefined;
    @Input() allowNegative = true;
    @Input() errorStateMatcher: ErrorStateMatcher | undefined;

    private _decimalPlaces = TWO_DECIMAL_PLACES;

    @Input()
    set decimalPlaces(value: number) {
        // Update internal property
        this._decimalPlaces = value;

        // If there is a current value, re-round it to the new decimals
        if (this.value != null) {
            const newValue = NumberUtils.roundNumber(this.value, false, this._decimalPlaces);
            // Update form control *without* triggering another input event
            this.inputValueControl.setValue(newValue, { emitEvent: false });

            // If you need to propagate this change to parent form(s) or watchers:
            if (this._onChange) {
                this._onChange(newValue);
            }

            this.stateChanges.next();
        }
    }

    get decimalPlaces(): number {
        return this._decimalPlaces;
    }

    @Input() set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    get placeholder(): string {
        return this._placeholder;
    }

    @Input() set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    get required(): boolean {
        if (this.ngControl != null && this.ngControl.control != null) {
            return this.ngControl.control.hasValidator(Validators.required);
        } else {
            return this._required;
        }
    }

    @Input() set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.inputValueControl.disable() : this.inputValueControl.enable();
        this.stateChanges.next();
    }

    get disabled(): boolean {
        return this._disabled;
    }

    @Input() set value(numberValue: number | null) {
        let roundedNumberValue: number | null = null;
        if (numberValue != null && NumberUtils.isFinite(numberValue)) {
            roundedNumberValue = NumberUtils.roundNumber(numberValue, false, this.decimalPlaces);
        }
        this.inputValueControl.setValue(roundedNumberValue);
        this.stateChanges.next();
    }

    get value(): number | null {
        return this.inputValueControl.value;
    }
    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,
        @Optional() _parentForm: NgForm,
        @Optional() _parentFormGroup: FormGroupDirective,
        @Optional() @Self() public readonly ngControl: NgControl,
    ) {
        this.addSvgIcon('aumico-pattern-icon-alert-triangle');
        this.parentForm = _parentFormGroup || _parentForm || null;

        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    get empty(): boolean {
        return !new BigNumber(this.inputValueControl.value).isFinite();
    }

    get shouldLabelFloat(): boolean {
        return this.focused || !this.empty;
    }

    get errorState(): boolean {
        let hasError: boolean;
        if (this.errorStateMatcher != null) {
            const control = this.ngControl != null ? this.ngControl.control : null;
            hasError = this.errorStateMatcher.isErrorState(control, this.parentForm);
        } else {
            const isControlInvalid = this.ngControl?.invalid == null ? false : this.ngControl.invalid;
            hasError = this.touched && isControlInvalid;
        }

        this.displayWarningTriangle = hasError;
        return hasError;
    }

    onFocusIn(): void {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent): void {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            if (this._onTouched != null) {
                this._onTouched();
            }
            this.stateChanges.next();
        }
    }

    /** Implemented as part of MatFormFieldControl. */
    setDescribedByIds(ids: string[]): void {
        const controlElement = this._elementRef.nativeElement.querySelector('.number-input-container')!;
        controlElement.setAttribute(ARIA_DESCRIBED_BY, ids.join(' '));
    }

    /** Implemented as part of MatFormFieldControl. */
    onContainerClick(): void {
        // No implementation as there is no logic to apply when form field is clicked
        // Need to have it as it is an abstract method
    }

    /** Implemented as part of ControlValueAccessor. */
    writeValue(value: number | null): void {
        if (value == null) {
            this.value = null;
            return;
        }

        if (!NumberUtils.isFinite(value)) {
            this.value = null;
            return;
        }
        this.value = value;
    }

    /** Implemented as part of ControlValueAccessor. */
    registerOnChange(onChange: (value: number | null) => void): void {
        this._onChange = onChange;
    }

    /** Implemented as part of ControlValueAccessor. */
    registerOnTouched(onTouched: () => void): void {
        this._onTouched = onTouched;
    }

    /** Implemented as part of ControlValueAccessor. */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    onBeforeInput(event: InputEvent): boolean {
        if (event.target == null) {
            return false;
        }

        const newChar = event.data;
        if (newChar == null) {
            return true;
        }

        // Replace commas with dots
        if (newChar === ',') {
            event.preventDefault(); // Prevent the comma from being inserted
            const htmlInputElement = event.target as HTMLInputElement;
            const currentInputValue = htmlInputElement.value;
            const currentSelectionStartIndex = htmlInputElement.selectionStart as number;
            const currentSelectionEndIndex = htmlInputElement.selectionEnd as number;

            const newValue = this.getNewValueBasedOnSelectionRange(
                currentInputValue,
                '.',
                currentSelectionStartIndex,
                currentSelectionEndIndex,
            );

            htmlInputElement.value = newValue;

            if (this._onChange != null) {
                const bigNumber = new BigNumber(newValue);
                const valueToEmit = bigNumber.isFinite() ? bigNumber.toNumber() : null;
                this._onChange(valueToEmit);
            }

            return false;
        }

        const htmlInputElement = event.target as HTMLInputElement;

        const currentInputValue = htmlInputElement.value;

        if (newChar === '.' && this.decimalPlaces === NO_DECIMAL_PLACES) {
            return false;
        }

        if (newChar === '-' && !this.allowNegative) {
            return false;
        }

        if (currentInputValue === '' && (newChar === '-' || newChar === '.')) {
            return true;
        }

        if (currentInputValue === '-' && newChar === '.') {
            return true;
        }

        const currentSelectionStartIndex = htmlInputElement.selectionStart as number;
        const currentSelectionEndIndex = htmlInputElement.selectionEnd as number;

        const newValue = this.getNewValueBasedOnSelectionRange(
            currentInputValue,
            newChar,
            currentSelectionStartIndex,
            currentSelectionEndIndex,
        );
        const bigNumber = new BigNumber(newValue);
        if (!NumberUtils.isFinite(bigNumber)) {
            return false;
        }

        const trimmedBigNumber = NumberUtils.roundNumber(bigNumber, false, this.decimalPlaces, BigNumber.ROUND_DOWN);
        return NumberUtils.areNumbersEqual(trimmedBigNumber, bigNumber);
    }

    onInput(event: Event): void {
        if (event.target == null || this._onChange == null) {
            return;
        }

        const htmlInputElement = event.target as HTMLInputElement;
        const currentInputValue = htmlInputElement.value;
        const bigNumber = new BigNumber(currentInputValue);

        if (!bigNumber.isFinite()) {
            this._onChange(null);
            return;
        }

        const decimalPointIndex = currentInputValue.indexOf('.');
        let decimalPartOfCurrentInputValue: string | undefined;
        let zeroDecimalPartBasedOnDecimalPlaces: string | undefined;
        if (this.decimalPlaces !== NO_DECIMAL_PLACES && decimalPointIndex !== -1) {
            decimalPartOfCurrentInputValue = currentInputValue.substring(decimalPointIndex);
            zeroDecimalPartBasedOnDecimalPlaces = '.';

            for (let i = 0; i < this.decimalPlaces - 1; i++) {
                zeroDecimalPartBasedOnDecimalPlaces = zeroDecimalPartBasedOnDecimalPlaces + '0';
            }
        }

        let newInputValue: string;
        if (
            zeroDecimalPartBasedOnDecimalPlaces != null &&
            decimalPartOfCurrentInputValue != null &&
            zeroDecimalPartBasedOnDecimalPlaces.startsWith(decimalPartOfCurrentInputValue)
        ) {
            newInputValue = bigNumber.toString() + decimalPartOfCurrentInputValue;
        } else {
            newInputValue = bigNumber.toString();
        }

        htmlInputElement.value = newInputValue;

        const valueToEmit = bigNumber.toNumber();

        if (newInputValue.endsWith('.')) {
            return;
        }

        this._onChange(valueToEmit);
    }

    private getNewValueBasedOnSelectionRange(
        currentString: string,
        newChar: string,
        selectionStartIndex: number,
        selectionEndIndex: number,
    ): string {
        return currentString.substring(0, selectionStartIndex) + newChar + currentString.substring(selectionEndIndex);
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }
}
